import { useCallback, useEffect, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { Rec } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  ConfigProvider,
  Empty,
  Form,
  GetProp,
  Space,
  Table,
  Select,
  InputNumber,
} from "antd";
import IconButton from "../../common/IconButton";
import { PagedResults } from "../../types/generics";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";

export default function RecsList() {
  const { showToast } = useToasts();
  const [recs, setRecs] = useState<Rec[]>();
  const [error, setError] = useState(false);
  const [totalRecs, setTotalRecs] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
  }>({
    pageSize: 20,
    currentPage: 1,
  });
  const [unit, setUnit] = useState<"seconds" | "minutes">("seconds"); // 👈 visualizzazione
  const [addRecModal, setAddRecModal] = useState(false);
  const [deleteRecModal, setDeleteRecModal] = useState(false);
  const [recForm] = Form.useForm();
  const [selectedRec, setSelectedRec] = useState<Rec>();

  const initRecs = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);

    Entities.getPaginatedRecs(urlParams).then(
      ({ data }) => {
        setRecs((data as PagedResults<Rec>).items);
        setTotalRecs((data as PagedResults<Rec>).totalItems);
      },
      () => {
        showToast(
          "recs-error",
          "Impossibile ottenere la lista dei recuperi",
          "",
          "danger"
        );
        setRecs([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setRecs(undefined);
    initRecs();
  }, [initRecs]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({ ...previous, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({ ...previous, pageSize: size, currentPage: 1 }));
  }, []);

  const handleEditRecClick = useCallback(
    (rec?: Rec) => () => {
      setSelectedRec(rec);
      setAddRecModal(true);
    },
    []
  );

  const handleDeleteRecClick = useCallback(
    (rec: Rec) => () => {
      setSelectedRec(rec);
      setDeleteRecModal(true);
    },
    []
  );

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Recupero"
        count={totalRecs}
        actions={
          <button onClick={handleEditRecClick()} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Recupero</span>
          </button>
        }
      >
        <span className="me-2 fw-semibold">Visualizza in:</span>
        <Select
          value={unit}
          onChange={setUnit}
          className="me-3"
          style={{ width: 120 }}
          options={[
            { value: "seconds", label: "Secondi" },
            { value: "minutes", label: "Minuti" },
          ]}
        />
      </ListPageHeader>
    );
  }, [unit, handleEditRecClick, totalRecs]);

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessun Recupero" />
  );

  const formatRec = useCallback(
    (recSeconds: number) => {
      if (unit === "seconds") return `${recSeconds} sec`;
      if (unit === "minutes") {
        const m = Math.floor(recSeconds / 60);
        const s = recSeconds % 60;
        return `${m}:${s.toString().padStart(2, "0")} min`;
      }
    },
    [unit]
  );

  const renderRecs = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        dataSource={recs}
        rowKey="id"
        locale={{ emptyText: renderEmpty() }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalRecs,
        }}
      >
        <Table.Column
          key="rec"
          title="Recupero"
          className="text-center col-pattern"
          render={(_value, record: Rec) => formatRec(record.rec)}
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: Rec) => (
            <Space size={1}>
              <IconButton
                tooltip="Modifica"
                onClick={handleEditRecClick(record)}
              >
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteRecClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    recs,
    error,
    filters,
    totalRecs,
    handlePageChange,
    handlePageSizeChange,
    handleEditRecClick,
    handleDeleteRecClick,
    formatRec,
  ]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!recs}>{renderRecs()}</ListPanel>;
  }, [recs, renderRecs]);

  const handleAddRecModalClose = useCallback(() => {
    setAddRecModal(false);
    recForm.resetFields();
    setSelectedRec(undefined);
  }, [recForm]);

  const handleDeleteRecModalClose = useCallback(() => {
    setDeleteRecModal(false);
    setSelectedRec(undefined);
  }, []);

  const handleEditRecSubmit = useCallback(
    (values: any) => {
      setLoading(true);

      const body = { ...values };
      const promise = selectedRec
        ? Entities.updateRec(selectedRec.id!, body)
        : Entities.createRec(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "rec-success",
            `Recupero ${selectedRec ? "Modificato" : "Creato"} correttamente`,
            undefined,
            "success"
          );
          handleAddRecModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "rec-error",
            `Errore nella ${
              selectedRec ? "modifica" : "creazione"
            } del Recupero`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedRec, showToast, handleAddRecModalClose, reset]
  );

  const handleDeleteRec = useCallback(() => {
    setLoading(true);
    Entities.deleteRec(selectedRec!.id!).then(
      () => {
        setLoading(false);
        handleDeleteRecModalClose();
        reset();
        showToast(
          "delete-rec-success",
          "Recupero correttamente eliminato",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-rec-error",
          "Errore nella cancellazione del Recupero, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [selectedRec, showToast, handleDeleteRecModalClose, reset]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      {/* MODALE AGGIUNTA / MODIFICA */}
      <Modal show={addRecModal} onHide={handleAddRecModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedRec ? "Modifica" : "Nuovo"} Recupero
          </Modal.Title>
        </Modal.Header>
        <Form form={recForm} onFinish={handleEditRecSubmit}>
          <Modal.Body>
            <Form.Item
              label="Recupero (in secondi)"
              name="rec"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              onClick={handleAddRecModalClose}
              variant="light"
              size="sm"
              className="border me-2"
            >
              Annulla
            </Button>
            <Button variant="success" size="sm" type="submit">
              {loading ? (
                <TailSpin width={15} height={15} color="#9c1419" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  <span className="ms-2">Salva</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE ELIMINAZIONE */}
      <Modal show={deleteRecModal} onHide={handleDeleteRecModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Recupero</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare il recupero{" "}
            <strong>
              {selectedRec &&
                (unit === "minutes"
                  ? `${(selectedRec.rec / 60).toFixed(2)} min`
                  : `${selectedRec.rec} sec`)}
            </strong>
            ?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteRecModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteRec}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
